import React, { useState, useEffect, useCallback } from 'react';
import { pixApi } from '../lib/api';
import { formatCurrency, formatDate, copyToClipboard } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Pencil, Loader2, KeyRound, QrCode, Copy, CheckCircle2, Star, Download, Eye, Link2, ExternalLink
} from 'lucide-react';

const TIPOS_CHAVE = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'telefone', label: 'Telefone' },
  { value: 'aleatoria', label: 'Chave aleatória' }
];

const emptyKey = {
  nome: '',
  tipo: 'email',
  chave: '',
  favorecido_nome: '',
  favorecido_cidade: 'BELO HORIZONTE',
  is_default: false
};

const emptyCobranca = {
  pix_key_id: '',
  valor: 0,
  descricao: '',
  cliente_nome: '',
  ordem_id: null,
  orcamento_id: null
};

const Pix = () => {
  const [keys, setKeys] = useState([]);
  const [cobrancas, setCobrancas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Key form
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [keyData, setKeyData] = useState({ ...emptyKey });
  const [savingKey, setSavingKey] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  // Cobrança form + result
  const [showCobrancaForm, setShowCobrancaForm] = useState(false);
  const [cobrancaData, setCobrancaData] = useState({ ...emptyCobranca });
  const [savingCobranca, setSavingCobranca] = useState(false);
  const [pixResult, setPixResult] = useState(null);
  const [cobrancaToDelete, setCobrancaToDelete] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [k, c] = await Promise.all([pixApi.listKeys(), pixApi.listCobrancas()]);
      setKeys(k.data);
      setCobrancas(c.data);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar dados PIX');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const defaultKey = keys.find((k) => k.is_default) || keys[0];

  // ============ KEY HANDLERS ============
  const handleOpenKeyForm = (k = null) => {
    if (k) {
      setSelectedKey(k);
      setKeyData({
        nome: k.nome,
        tipo: k.tipo,
        chave: k.chave,
        favorecido_nome: k.favorecido_nome,
        favorecido_cidade: k.favorecido_cidade,
        is_default: k.is_default
      });
    } else {
      setSelectedKey(null);
      setKeyData({ ...emptyKey, is_default: keys.length === 0 });
    }
    setShowKeyForm(true);
  };

  const handleSaveKey = async (e) => {
    e.preventDefault();
    if (!keyData.nome.trim() || !keyData.chave.trim() || !keyData.favorecido_nome.trim()) {
      toast.error('Preencha nome, chave e favorecido');
      return;
    }
    setSavingKey(true);
    try {
      if (selectedKey) {
        await pixApi.updateKey(selectedKey.id, keyData);
        toast.success('Chave atualizada!');
      } else {
        await pixApi.createKey(keyData);
        toast.success('Chave cadastrada!');
      }
      setShowKeyForm(false);
      setSelectedKey(null);
      setKeyData({ ...emptyKey });
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar chave');
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete) return;
    try {
      await pixApi.deleteKey(keyToDelete.id);
      toast.success('Chave excluída');
      setKeyToDelete(null);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir chave');
    }
  };

  // ============ COBRANCA HANDLERS ============
  const handleOpenCobrancaForm = () => {
    if (keys.length === 0) {
      toast.error('Cadastre uma chave PIX primeiro');
      return;
    }
    setCobrancaData({
      ...emptyCobranca,
      pix_key_id: defaultKey?.id || keys[0].id
    });
    setShowCobrancaForm(true);
  };

  const handleGeneratePix = async (e) => {
    e.preventDefault();
    if (!cobrancaData.pix_key_id) {
      toast.error('Selecione a chave PIX');
      return;
    }
    if (!cobrancaData.valor || cobrancaData.valor <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    setSavingCobranca(true);
    try {
      const res = await pixApi.createCobranca(cobrancaData);
      setPixResult(res.data);
      setShowCobrancaForm(false);
      setCobrancaData({ ...emptyCobranca });
      loadData();
      toast.success('PIX gerado com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao gerar PIX');
    } finally {
      setSavingCobranca(false);
    }
  };

  const handleOpenCobranca = async (c) => {
    try {
      const res = await pixApi.getCobranca(c.id);
      setPixResult(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar cobrança');
    }
  };

  const handleDeleteCobranca = async () => {
    if (!cobrancaToDelete) return;
    try {
      await pixApi.deleteCobranca(cobrancaToDelete.id);
      toast.success('Cobrança removida');
      setCobrancaToDelete(null);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir cobrança');
    }
  };

  const handleCopyBrcode = async (code) => {
    const ok = await copyToClipboard(code);
    if (ok) toast.success('Código PIX copiado!');
  };

  const handleDownloadQR = () => {
    if (!pixResult?.qr_code) return;
    const link = document.createElement('a');
    link.href = pixResult.qr_code;
    link.download = `pix-${pixResult.txid || 'cobranca'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShareWhatsApp = async () => {
    if (!pixResult) return;
    const paymentUrl = `${window.location.origin}/pagar/${pixResult.id}`;
    const msg = `*Pagamento via PIX - Tenance*\n\n` +
      `Olá${pixResult.cliente_nome ? ' ' + pixResult.cliente_nome : ''}!\n\n` +
      `Segue seu link de pagamento PIX:\n` +
      `${paymentUrl}\n\n` +
      `💰 *Valor:* ${formatCurrency(pixResult.valor)}\n` +
      `👤 *Favorecido:* ${pixResult.favorecido_nome}\n` +
      (pixResult.descricao ? `📝 *Descrição:* ${pixResult.descricao}\n` : '') +
      `\nOu use o PIX Copia e Cola:\n\`\`\`\n${pixResult.brcode}\n\`\`\`\n\n` +
      `Após o pagamento, envie o comprovante. Obrigado!`;
    const ok = await copyToClipboard(msg);
    if (ok) toast.success('Mensagem para WhatsApp copiada!');
  };

  const handleCopyLink = async () => {
    if (!pixResult) return;
    const paymentUrl = `${window.location.origin}/pagar/${pixResult.id}`;
    const ok = await copyToClipboard(paymentUrl);
    if (ok) toast.success('Link de pagamento copiado!');
  };

  return (
    <div className="space-y-6" data-testid="pix-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            PIX
          </h1>
          <p className="text-gray-500 mt-1">
            Gere links de pagamento PIX e gerencie suas chaves.
          </p>
        </div>
        <Button onClick={handleOpenCobrancaForm} data-testid="new-pix-btn">
          <QrCode size={18} className="mr-2" />
          Gerar PIX
        </Button>
      </div>

      <Tabs defaultValue="cobrancas" className="w-full">
        <TabsList>
          <TabsTrigger value="cobrancas" data-testid="tab-cobrancas">Cobranças</TabsTrigger>
          <TabsTrigger value="chaves" data-testid="tab-chaves">Chaves PIX</TabsTrigger>
        </TabsList>

        {/* ============ COBRANÇAS TAB ============ */}
        <TabsContent value="cobrancas">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Work Sans' }}>Histórico de Cobranças</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
              ) : cobrancas.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma cobrança gerada ainda
                </div>
              ) : (
                <div className="table-fit">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Cliente</th>
                        <th>Descrição</th>
                        <th>Chave</th>
                        <th>Valor</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cobrancas.map((c) => (
                        <tr key={c.id} data-testid={`cobranca-row-${c.id}`}>
                          <td className="whitespace-nowrap">{formatDate(c.created_at?.split('T')[0])}</td>
                          <td>{c.cliente_nome || '—'}</td>
                          <td className="max-w-xs truncate" title={c.descricao}>{c.descricao || '—'}</td>
                          <td className="text-sm text-gray-600">{c.pix_key_nome}</td>
                          <td className="font-semibold text-[#2B7A3D]">{formatCurrency(c.valor)}</td>
                          <td>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleOpenCobranca(c)} title="Ver QR/código" data-testid={`view-cobranca-${c.id}`}>
                                <Eye size={16} className="text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleCopyBrcode(c.brcode)} title="Copiar código PIX" data-testid={`copy-cobranca-${c.id}`}>
                                <Copy size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setCobrancaToDelete(c)} className="text-red-600 hover:text-red-700" data-testid={`delete-cobranca-${c.id}`}>
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ CHAVES TAB ============ */}
        <TabsContent value="chaves">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
                <KeyRound size={20} />
                Chaves PIX
              </CardTitle>
              <Button size="sm" onClick={() => handleOpenKeyForm()} data-testid="add-key-btn">
                <Plus size={16} className="mr-1" />
                Nova chave
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
              ) : keys.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Cadastre uma chave PIX para começar a gerar cobranças
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {keys.map((k) => (
                    <div key={k.id} className="p-4 border rounded-lg bg-white hover:shadow-sm transition" data-testid={`key-card-${k.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900">{k.nome}</p>
                            {k.is_default && (
                              <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-700 gap-1">
                                <Star size={10} /> Padrão
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 uppercase mt-0.5">{TIPOS_CHAVE.find((t) => t.value === k.tipo)?.label || k.tipo}</p>
                          <p className="text-sm text-gray-700 mt-2 font-mono truncate" title={k.chave}>{k.chave}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {k.favorecido_nome} — {k.favorecido_cidade}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenKeyForm(k)} data-testid={`edit-key-${k.id}`}>
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setKeyToDelete(k)} className="text-red-600 hover:text-red-700" data-testid={`delete-key-${k.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ CHAVE FORM DIALOG ============ */}
      <Dialog open={showKeyForm} onOpenChange={setShowKeyForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selectedKey ? 'Editar chave PIX' : 'Nova chave PIX'}
            </DialogTitle>
            <DialogDescription>
              Cadastre uma chave PIX da empresa para gerar cobranças.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveKey} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="key-nome">Identificação interna *</Label>
              <Input
                id="key-nome"
                value={keyData.nome}
                onChange={(e) => setKeyData({ ...keyData, nome: e.target.value })}
                placeholder="Ex: PIX PJ Nubank"
                required
                data-testid="key-nome-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tipo da chave *</Label>
                <Select value={keyData.tipo} onValueChange={(v) => setKeyData({ ...keyData, tipo: v })}>
                  <SelectTrigger data-testid="key-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_CHAVE.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="key-chave">Chave *</Label>
                <Input
                  id="key-chave"
                  value={keyData.chave}
                  onChange={(e) => setKeyData({ ...keyData, chave: e.target.value })}
                  placeholder="Digite a chave"
                  required
                  data-testid="key-chave-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="favorecido">Nome do favorecido *</Label>
              <Input
                id="favorecido"
                value={keyData.favorecido_nome}
                onChange={(e) => setKeyData({ ...keyData, favorecido_nome: e.target.value.toUpperCase() })}
                placeholder="TENANCE ASSISTENCIA"
                maxLength={25}
                required
                data-testid="key-favorecido-input"
              />
              <p className="text-xs text-gray-500">Aparece no app do pagador. Max 25 caracteres, sem acentos.</p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="cidade">Cidade *</Label>
              <Input
                id="cidade"
                value={keyData.favorecido_cidade}
                onChange={(e) => setKeyData({ ...keyData, favorecido_cidade: e.target.value.toUpperCase() })}
                placeholder="BELO HORIZONTE"
                maxLength={15}
                required
                data-testid="key-cidade-input"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={keyData.is_default}
                onChange={(e) => setKeyData({ ...keyData, is_default: e.target.checked })}
                className="w-4 h-4"
                data-testid="key-default-checkbox"
              />
              <span className="text-sm">Marcar como chave padrão</span>
            </label>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowKeyForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={savingKey} className="bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white" data-testid="save-key-btn">
                {savingKey ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</> : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ COBRANCA FORM DIALOG ============ */}
      <Dialog open={showCobrancaForm} onOpenChange={setShowCobrancaForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>Gerar cobrança PIX</DialogTitle>
            <DialogDescription>
              Selecione a chave e informe o valor. Um QR Code e código copia-e-cola serão gerados.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleGeneratePix} className="space-y-4">
            <div className="space-y-1">
              <Label>Chave PIX *</Label>
              <Select
                value={cobrancaData.pix_key_id}
                onValueChange={(v) => setCobrancaData({ ...cobrancaData, pix_key_id: v })}
              >
                <SelectTrigger data-testid="cobranca-key-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {keys.map((k) => (
                    <SelectItem key={k.id} value={k.id}>
                      {k.nome} {k.is_default && '⭐'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="valor">Valor (R$) *</Label>
                <Input
                  id="valor"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={cobrancaData.valor}
                  onChange={(e) => setCobrancaData({ ...cobrancaData, valor: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="cobranca-valor-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cliente">Cliente</Label>
                <Input
                  id="cliente"
                  value={cobrancaData.cliente_nome}
                  onChange={(e) => setCobrancaData({ ...cobrancaData, cliente_nome: e.target.value })}
                  placeholder="Nome do cliente (opcional)"
                  data-testid="cobranca-cliente-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="desc">Descrição</Label>
              <Textarea
                id="desc"
                value={cobrancaData.descricao}
                onChange={(e) => setCobrancaData({ ...cobrancaData, descricao: e.target.value })}
                placeholder="Ex: OS #123 - Pintura sala"
                rows={2}
                maxLength={25}
                data-testid="cobranca-desc-input"
              />
              <p className="text-xs text-gray-500">Aparece no comprovante. Max 25 caracteres, sem acentos.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCobrancaForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={savingCobranca} className="bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white" data-testid="generate-pix-btn">
                {savingCobranca ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando...</> : (
                  <><QrCode className="w-4 h-4 mr-2" />Gerar PIX</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ RESULT DIALOG ============ */}
      <Dialog open={!!pixResult} onOpenChange={(open) => !open && setPixResult(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <CheckCircle2 className="text-emerald-600" size={22} />
              PIX gerado!
            </DialogTitle>
            <DialogDescription>
              Compartilhe o QR Code ou o código copia-e-cola com o cliente.
            </DialogDescription>
          </DialogHeader>

          {pixResult && (
            <div className="space-y-4">
              <div className="flex justify-center bg-gray-50 rounded-xl p-4">
                {pixResult.qr_code && (
                  <img src={pixResult.qr_code} alt="QR Code" className="w-56 h-56" data-testid="pix-qr-image" />
                )}
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 mb-0.5">Valor a receber</p>
                <p className="text-2xl font-bold text-[#2B7A3D] tabular-nums">{formatCurrency(pixResult.valor)}</p>
                {pixResult.descricao && (
                  <p className="text-xs text-gray-600 mt-1">{pixResult.descricao}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label>Link de pagamento (para enviar ao cliente)</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/pagar/${pixResult.id}`}
                    className="font-mono text-xs bg-gray-50"
                    onClick={(e) => e.target.select()}
                    data-testid="pix-payment-link"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopyLink}
                    title="Copiar link"
                    data-testid="copy-payment-link-btn"
                  >
                    <Link2 size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.open(`/pagar/${pixResult.id}`, '_blank')}
                    title="Abrir em nova aba"
                    data-testid="open-payment-link-btn"
                  >
                    <ExternalLink size={16} />
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <Label>PIX Copia e Cola</Label>
                <Textarea
                  readOnly
                  value={pixResult.brcode}
                  rows={3}
                  className="font-mono text-xs bg-gray-50"
                  data-testid="pix-brcode-textarea"
                  onClick={(e) => e.target.select()}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button
                  onClick={() => handleCopyBrcode(pixResult.brcode)}
                  className="bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white"
                  data-testid="copy-brcode-btn"
                >
                  <Copy size={16} className="mr-1" /> Copiar
                </Button>
                <Button variant="outline" onClick={handleDownloadQR} data-testid="download-qr-btn">
                  <Download size={16} className="mr-1" /> Baixar QR
                </Button>
                <Button variant="outline" onClick={handleShareWhatsApp} data-testid="whatsapp-share-btn">
                  <Copy size={16} className="mr-1" /> WhatsApp
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmations */}
      <AlertDialog open={!!keyToDelete} onOpenChange={(o) => !o && setKeyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir chave PIX</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir &quot;{keyToDelete?.nome}&quot;? Cobranças já geradas não serão afetadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteKey} className="bg-red-600 hover:bg-red-700 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cobrancaToDelete} onOpenChange={(o) => !o && setCobrancaToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cobrança</AlertDialogTitle>
            <AlertDialogDescription>
              Remover esta cobrança do histórico? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCobranca} className="bg-red-600 hover:bg-red-700 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Pix;
